/**
 * DocsTab — Main documentation tab container.
 *
 * States:
 *   1. No search query -> show ContextualSuggestions
 *   2. Searching -> show DocResults
 *   3. Result selected -> show DocViewer
 */
import { createElement, useState, useCallback, useMemo } from 'react';
import { useWidgetConfig } from '../../context';
import { useDocSearch } from '../../hooks/useDocSearch';
import { DocSearchBar } from './DocSearchBar';
import { ContextualSuggestions } from './ContextualSuggestions';
import { DocResults } from './DocResults';
import { DocViewer } from './DocViewer';
import type { DocSection, DocSearchResult } from '../../types';

type ViewState =
  | { mode: 'idle' }
  | { mode: 'searching'; query: string }
  | { mode: 'viewing'; section: DocSection | null; searchResult: DocSearchResult };

/** Flatten nested docs for lookup by ID */
function buildDocMap(sections: DocSection[]): Map<string, DocSection> {
  const map = new Map<string, DocSection>();
  function walk(items: DocSection[]) {
    for (const item of items) {
      map.set(item.id, item);
      if (item.children) walk(item.children);
    }
  }
  walk(sections);
  return map;
}

export function DocsTab() {
  const config = useWidgetConfig();
  const docs = config.docs ?? [];
  const charlotte = config.charlotte;

  const { results, loading, search, clear } = useDocSearch({
    charlotte,
    docs,
  });

  const [view, setView] = useState<ViewState>({ mode: 'idle' });

  const docMap = useMemo(() => buildDocMap(docs), [docs]);

  const handleSearch = useCallback(
    (query: string) => {
      setView({ mode: 'searching', query });
      search(query);
    },
    [search],
  );

  const handleClear = useCallback(() => {
    setView({ mode: 'idle' });
    clear();
  }, [clear]);

  const handleSelectResult = useCallback(
    (result: DocSearchResult) => {
      const section = docMap.get(result.sectionId) ?? null;
      setView({ mode: 'viewing', section, searchResult: result });
    },
    [docMap],
  );

  const handleSelectSuggestion = useCallback((section: DocSection) => {
    setView({
      mode: 'viewing',
      section,
      searchResult: { sectionId: section.id, title: section.title, snippet: '', score: 1 },
    });
  }, []);

  const handleBack = useCallback(() => {
    if (view.mode === 'viewing') {
      // Go back to search results if we had a query, otherwise idle
      setView({ mode: 'idle' });
    }
  }, [view.mode]);

  // When viewing a doc, show DocViewer only
  if (view.mode === 'viewing') {
    return createElement(
      'div',
      {
        style: {
          display: 'flex',
          flexDirection: 'column' as const,
          height: '100%',
          padding: '12px',
          boxSizing: 'border-box' as const,
        },
      },
      createElement(DocViewer, {
        section: view.section,
        searchResult: view.searchResult,
        onBack: handleBack,
      }),
    );
  }

  if (!config.docs?.length && !charlotte?.endpoint) {
    return createElement(
      'div',
      {
        style: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '24px',
          color: 'var(--sw-text-secondary)',
          fontSize: '14px',
          textAlign: 'center' as const,
        },
      },
      'Documentation is not configured for this product.',
    );
  }

  return createElement(
    'div',
    {
      style: {
        display: 'flex',
        flexDirection: 'column' as const,
        height: '100%',
        padding: '12px',
        boxSizing: 'border-box' as const,
        overflow: 'hidden',
      },
    },
    // Search bar
    createElement(DocSearchBar, {
      productName: config.productName,
      onSearch: handleSearch,
      onClear: handleClear,
    }),
    // Content area
    createElement(
      'div',
      {
        style: {
          flex: 1,
          overflowY: 'auto' as const,
          marginTop: '8px',
        },
      },
      view.mode === 'searching'
        ? createElement(DocResults, {
            results,
            loading,
            onSelect: handleSelectResult,
          })
        : createElement(ContextualSuggestions, {
            docs,
            currentRoute: undefined, // Phase 1-2 will wire this from widget config
            onSelect: handleSelectSuggestion,
          }),
    ),
  );
}
